import React, { useEffect, useState, useMemo } from 'react';
import { Dialog, TextInputField, Button, SelectMenu, Spinner, Pane, Avatar, Text, Label, TextareaField } from 'evergreen-ui';
import { db } from 'src/firebase';
import { getDocs, collection, query } from 'firebase/firestore';

const SendEmailModal = (props) => {
  const { isOpen, isEmailSendInProgress, initialSubject, onClose, onSend } = props;

  const [subscribers, setSubscribers] = useState([]);
  const [areSubscribersLoading, setAreSubscribersLoading] = useState(true);
  const getSubscribers = async () => {
    const q = await query(collection(db, 'subscribers'));
    const snapshot = await getDocs(q);
    setSubscribers(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    setAreSubscribersLoading(false);
  };

  useEffect(() => {
    getSubscribers();
  }, []);

  const [subject, setSubject] = useState(initialSubject || '');
  const [text, setText] = useState('');
  const [selectedSubscribers, setSelectedSubscribers] = useState([]);
  const { categoryOpts, allOptions } = useMemo(() => {
    const indivOpts = subscribers
      .map((subscriber) => ({
        label: subscriber.email,
        value: subscriber.id,
      }))
      .filter((o) => o.value !== undefined);

    const categoryOpts = Array.from(new Set(subscribers.map((subscriber) => subscriber.category)))
      .map((category) => ({
        label: category,
        value: category,
      }))
      .filter((o) => o.value !== undefined);

    return {
      indivOpts,
      categoryOpts,
      allOptions: [
        {
          label: 'All Subscribers',
          value: 'All',
        },
        ...categoryOpts,
        ...indivOpts,
      ],
    };
  }, [subscribers]);

  const isFormValid = useMemo(() => {
    const isSubjectValid = subject.length > 0;
    const isRecipientValid = selectedSubscribers.length > 0;

    return isSubjectValid && isRecipientValid;
  }, [selectedSubscribers.length, subject.length]);

  const onSendWrapper = () => {
    const selected = selectedSubscribers.reduce((acc, curr) => {
      const isCategory = categoryOpts.find((c) => c.value === curr);
      if (isCategory) {
        const allSubscribersInCategory = subscribers.filter((s) => s.category === curr).map((s) => s.email);
        return [...acc, ...allSubscribersInCategory];
      }

      // if 'All' is selected, return all subscribers
      if (curr === 'All') {
        return subscribers.map((s) => s.email);
      }

      return [...acc, curr];
    }, []);

    const uniqueSelected = Array.from(new Set(selected));

    onSend({ to: uniqueSelected.join(', '), subject, text });
  };

  return (
    <Dialog
      isShown={isOpen}
      title={'Send Email'}
      onCancel={onClose}
      onCloseComplete={onClose}
      onConfirm={onSendWrapper}
      confirmLabel="Send"
      isConfirmDisabled={!isFormValid}
      isConfirmLoading={areSubscribersLoading || isEmailSendInProgress}
    >
      {areSubscribersLoading ? (
        <Spinner />
      ) : (
        <>
          <TextInputField label="Subject line" required value={subject} onChange={(e) => setSubject(e.target.value)} />
          <TextareaField
            label="Plaintext body"
            placeholder="This will be sent as plain text, and show as the preview in most email clients"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <Label marginBottom="8px" display="block">
            Recipients *
          </Label>
          <SelectMenu
            isMultiSelect
            title="Select multiple names"
            options={allOptions}
            selected={selectedSubscribers}
            onSelect={(item) => {
              const selected = [...selectedSubscribers, item.value];
              const selectedItems = selected;
              setSelectedSubscribers(selectedItems);
            }}
            onDeselect={(item) => {
              const deselectedItemIndex = selectedSubscribers.indexOf(item.value);
              const selectedItems = selectedSubscribers.filter((_item, i) => i !== deselectedItemIndex);
              setSelectedSubscribers(selectedItems);
            }}
          >
            <Button width="100%">Select subscribers</Button>
          </SelectMenu>
          <Pane marginTop="8px">
            {selectedSubscribers.map((s) => (
              <Pane
                key={s}
                display="flex"
                alignItems="center"
                gap="12px"
                cursor="pointer"
                width="fit-content"
                onClick={() => {
                  const deselectedItemIndex = selectedSubscribers.indexOf(s);
                  const selectedItems = selectedSubscribers.filter((_item, i) => i !== deselectedItemIndex);
                  setSelectedSubscribers(selectedItems);
                }}
              >
                <Avatar name={s} size={18} />
                <Text>{s}</Text>
              </Pane>
            ))}
          </Pane>
        </>
      )}
    </Dialog>
  );
};

export default SendEmailModal;
